const API_BASE = "/api/admin";

// Tipos
export interface Shop {
  id: string;
  name: string;
  owner: string;
  username: string;
  accountStatus: "trial" | "active" | "suspended" | "canceled";
  trialStartDate: string | null;
  trialEndDate: string | null;
  subscriptionStartDate: string | null;
  subscriptionEndDate: string | null;
  nextPaymentDate: string | null;
  paymentStatus: string;
  isVerified: boolean;
  notes: string | null;
  createdAt: string;
  productCount: string;
  recentSalesCount: string;
  customerCount: string;
}

export interface AdminStats {
  totalShops: number;
  activeShops: number;
  trialShops: number;
  suspendedShops: number;
  expiringSoon: number;
  overdueShops: number;
  monthlyRevenue: number;
}

export interface PaymentRecord {
  id: string;
  shopId: string;
  amount: string;
  currency: string;
  paymentDate: string;
  periodCovered: string;
  paymentMethod: string;
  referenceNumber?: string;
  receiptImageUrl?: string;
  verifiedByAdmin: boolean;
  adminNotes?: string;
  ownerId?: string;
  ownerBusinessName?: string;
  ownerEmail?: string;
}

export interface AdminLog {
  id: string;
  adminUserId: string;
  actionType: string;
  shopId?: string;
  details: any;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
  admin?: {
    username: string;
    fullName: string;
  };
}

// ============ NUEVOS TIPOS PARA OWNERS ============
export interface Owner {
  id: string;
  businessName: string;
  ownerName: string;
  email: string;
  phone: string | null;
  currency: string;
  planType: string;
  accountStatus: "trial" | "active" | "suspended" | "canceled";
  trialStartDate: string | null;
  trialEndDate: string | null;
  subscriptionStartDate: string | null;
  subscriptionEndDate: string | null;
  nextPaymentDate: string | null;
  paymentStatus: string;
  isVerified: boolean;
  notes: string | null;
  suspendedAt: string | null;
  createdAt: string;
  // Estadísticas agregadas
  shopsCount?: number;
  totalProducts?: number;
  totalSales?: number;
  totalRevenue?: number;
}

export interface OwnerFilters {
  status?: string;
  plan?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface OwnersResponse {
  data: Owner[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Servicio API
export const adminApi = {
  // Dashboard
  async getStats(): Promise<AdminStats> {
    const authHeader = localStorage.getItem("admin_auth");

    const response = await fetch(`${API_BASE}/dashboard/stats`, {
      headers: {
        Authorization: atob(authHeader || ""),
      },
    });

    if (!response.ok) {
      throw new Error("Error obteniendo estadísticas");
    }

    const data = await response.json();
    return data.data;
  },

  // Shops
  async getShops(filters?: {
    status?: string;
    search?: string;
    page?: number; // ← El frontend envía page
    limit?: number;
  }): Promise<{
    data: Shop[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const authHeader = localStorage.getItem("admin_auth");
    const params = new URLSearchParams();

    // CORRECCIÓN: Manejar todos los casos de status
    if (filters?.status !== undefined) {
      const statusValue = filters.status === "all" ? "" : filters.status;
      params.append("status", statusValue);
    }

    if (filters?.search) {
      params.append("search", filters.search);
    }

    // El backend espera page, no offset (porque calcula offset internamente)
    if (filters?.page) {
      params.append("page", filters.page.toString());
    }

    if (filters?.limit) {
      params.append("limit", filters.limit.toString());
    }

    // NOTA: NO enviamos offset, el backend lo calcula con: (page - 1) * limit

    const url = `${API_BASE}/shops${params.toString() ? "?" + params.toString() : ""}`;

    const response = await fetch(url, {
      headers: {
        Authorization: atob(authHeader || ""),
      },
    });

    if (!response.ok) {
      throw new Error("Error obteniendo negocios");
    }

    return await response.json();
  },

  async getShopDetails(shopId: string): Promise<any> {
    const authHeader = localStorage.getItem("admin_auth");

    const response = await fetch(`${API_BASE}/shops/${shopId}`, {
      headers: {
        Authorization: atob(authHeader || ""),
      },
    });

    if (!response.ok) {
      throw new Error("Error obteniendo detalles del negocio");
    }

    const data = await response.json();
    return data.data;
  },

  async activateShop(shopId: string, period: string): Promise<any> {
    const authHeader = localStorage.getItem("admin_auth");

    const response = await fetch(`${API_BASE}/shops/${shopId}/activate`, {
      method: "POST",
      headers: {
        Authorization: atob(authHeader || ""),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ period }),
    });

    if (!response.ok) {
      throw new Error("Error activando cuenta");
    }

    return await response.json();
  },

  async suspendShop(shopId: string, reason?: string): Promise<any> {
    const authHeader = localStorage.getItem("admin_auth");

    const response = await fetch(`${API_BASE}/shops/${shopId}/suspend`, {
      method: "POST",
      headers: {
        Authorization: atob(authHeader || ""),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ reason }),
    });

    if (!response.ok) {
      throw new Error("Error suspendiendo cuenta");
    }

    return await response.json();
  },

  async extendTrial(shopId: string, days: number): Promise<any> {
    const authHeader = localStorage.getItem("admin_auth");

    const response = await fetch(`${API_BASE}/shops/${shopId}/extend-trial`, {
      method: "POST",
      headers: {
        Authorization: atob(authHeader || ""),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ days }),
    });

    if (!response.ok) {
      throw new Error("Error extendiendo trial");
    }

    return await response.json();
  },

  async deleteShop(
    shopId: string,
  ): Promise<{ success: boolean; message: string }> {
    const authHeader = localStorage.getItem("admin_auth");

    const response = await fetch(`/api/shops/${shopId}`, {
      method: "DELETE",
      headers: {
        Authorization: atob(authHeader || ""),
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Error eliminando tienda");
    }

    return await response.json();
  },

  // Payments
  async registerPayment(
    shopId: string,
    paymentData: {
      amount: number;
      periodCovered: string;
      paymentMethod: string;
      referenceNumber?: string;
      receiptImageUrl?: string;
      adminNotes?: string;
    },
  ): Promise<PaymentRecord> {
    const authHeader = localStorage.getItem("admin_auth");

    const response = await fetch(`${API_BASE}/shops/${shopId}/payments`, {
      method: "POST",
      headers: {
        Authorization: atob(authHeader || ""),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(paymentData),
    });

    if (!response.ok) {
      throw new Error("Error registrando pago");
    }

    const data = await response.json();
    return data.data.payment;
  },

  async getShopPayments(shopId: string): Promise<PaymentRecord[]> {
    const authHeader = localStorage.getItem("admin_auth");

    const response = await fetch(`${API_BASE}/shops/${shopId}/payments`, {
      headers: {
        Authorization: atob(authHeader || ""),
      },
    });

    if (!response.ok) {
      throw new Error("Error obteniendo pagos");
    }

    const data = await response.json();
    return data.data;
  },

  // Logs
  // Logs CON PAGINACIÓN
  async getLogs(filters?: { page?: number; limit?: number }): Promise<{
    data: AdminLog[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const authHeader = localStorage.getItem("admin_auth");
    const params = new URLSearchParams();

    if (filters?.page) {
      params.append("page", filters.page.toString());
    }

    if (filters?.limit) {
      params.append("limit", filters.limit.toString());
    }

    const url = `${API_BASE}/logs${params.toString() ? "?" + params.toString() : ""}`;

    const response = await fetch(url, {
      headers: {
        Authorization: atob(authHeader || ""),
      },
    });

    if (!response.ok) {
      throw new Error("Error obteniendo logs");
    }

    return await response.json();
  },

  // Limpiar logs antiguos
  async cleanupOldLogs(daysToKeep: number = 30): Promise<{
    success: boolean;
    message: string;
    deletedCount: number;
    cutoffDate: string;
    daysKept: number;
  }> {
    const authHeader = localStorage.getItem("admin_auth");

    const response = await fetch(`${API_BASE}/logs/cleanup`, {
      method: "DELETE",
      headers: {
        Authorization: atob(authHeader || ""),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ daysToKeep }),
    });

    if (!response.ok) {
      throw new Error("Error limpiando logs antiguos");
    }

    return await response.json();
  },

  // Notifications
  async sendNotification(notificationData: {
    shopIds: string[] | "all";
    title: string;
    message: string;
    type?: "info" | "warning" | "error" | "payment" | "system";
    adminName: string;
  }): Promise<{ success: boolean; message: string; sentCount: number }> {
    const authHeader = localStorage.getItem("admin_auth");

    const response = await fetch(`${API_BASE}/notifications`, {
      method: "POST",
      headers: {
        Authorization: atob(authHeader || ""),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(notificationData),
    });

    if (!response.ok) {
      throw new Error("Error enviando notificación");
    }

    return await response.json();
  },

  async getShopNotifications(shopId: string): Promise<any[]> {
    const authHeader = localStorage.getItem("admin_auth");

    const response = await fetch(
      `${API_BASE}/shops/${shopId}/admin-notifications`,
      {
        headers: {
          Authorization: atob(authHeader || ""),
        },
      },
    );

    if (!response.ok) {
      throw new Error("Error obteniendo notificaciones");
    }

    const data = await response.json();
    return data.data;
  },

  // ============ NUEVOS MÉTODOS PARA GESTIÓN DE OWNERS ============

  /**
   * Obtener lista de owners con filtros y paginación
   */
  async getOwners(filters?: OwnerFilters): Promise<OwnersResponse> {
    const authHeader = localStorage.getItem("admin_auth");
    const params = new URLSearchParams();

    if (filters?.status !== undefined) {
      const statusValue = filters.status === "all" ? "" : filters.status;
      params.append("status", statusValue);
    }

    if (filters?.plan !== undefined) {
      const planValue = filters.plan === "all" ? "" : filters.plan;
      params.append("plan", planValue);
    }

    if (filters?.search) {
      params.append("search", filters.search);
    }

    if (filters?.page) {
      params.append("page", filters.page.toString());
    }

    if (filters?.limit) {
      params.append("limit", filters.limit.toString());
    }

    const url = `/api/admin/owners${params.toString() ? "?" + params.toString() : ""}`;

    const response = await fetch(url, {
      headers: {
        Authorization: atob(authHeader || ""),
      },
    });

    if (!response.ok) {
      throw new Error("Error obteniendo negocios");
    }

    return await response.json();
  },

  /**
   * Obtener detalles de un owner específico
   */
  async getOwnerDetails(ownerId: string): Promise<Owner> {
    const authHeader = localStorage.getItem("admin_auth");

    const response = await fetch(`/api/admin/owners/${ownerId}`, {
      headers: {
        Authorization: atob(authHeader || ""),
      },
    });

    if (!response.ok) {
      throw new Error("Error obteniendo detalles del negocio");
    }

    const data = await response.json();
    return data.data;
  },

  /**
   * Activar cuenta de owner (con todas sus tiendas)
   */
  async activateOwner(ownerId: string, period: string): Promise<any> {
    const authHeader = localStorage.getItem("admin_auth");

    const response = await fetch(`/api/admin/owners/${ownerId}/activate`, {
      method: "POST",
      headers: {
        Authorization: atob(authHeader || ""),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ period }),
    });

    if (!response.ok) {
      throw new Error("Error activando cuenta");
    }

    return await response.json();
  },

  /**
   * Suspender cuenta de owner (con todas sus tiendas)
   */
  async suspendOwner(ownerId: string, reason?: string): Promise<any> {
    const authHeader = localStorage.getItem("admin_auth");

    const response = await fetch(`/api/admin/owners/${ownerId}/suspend`, {
      method: "POST",
      headers: {
        Authorization: atob(authHeader || ""),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ reason }),
    });

    if (!response.ok) {
      throw new Error("Error suspendiendo cuenta");
    }

    return await response.json();
  },

  /**
   * Extender trial de owner
   */
  async extendOwnerTrial(ownerId: string, days: number): Promise<any> {
    const authHeader = localStorage.getItem("admin_auth");

    const response = await fetch(`/api/admin/owners/${ownerId}/extend-trial`, {
      method: "POST",
      headers: {
        Authorization: atob(authHeader || ""),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ days }),
    });

    if (!response.ok) {
      throw new Error("Error extendiendo trial");
    }

    return await response.json();
  },

  /**
   * Eliminar owner permanentemente (con todas sus tiendas y datos)
   */
  async deleteOwner(
    ownerId: string,
  ): Promise<{ success: boolean; message: string }> {
    const authHeader = localStorage.getItem("admin_auth");

    const response = await fetch(`/api/admin/owners/${ownerId}`, {
      method: "DELETE",
      headers: {
        Authorization: atob(authHeader || ""),
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Error eliminando negocio");
    }

    return await response.json();
  },

  /**
   * Obtener pagos de un owner
   */
  async getOwnerPayments(ownerId: string): Promise<any[]> {
    const authHeader = localStorage.getItem("admin_auth");

    const response = await fetch(`/api/admin/owners/${ownerId}/payments`, {
      headers: {
        Authorization: atob(authHeader || ""),
      },
    });

    if (!response.ok) {
      throw new Error("Error obteniendo pagos");
    }

    const data = await response.json();
    return data.data;
  },

  /**
   * Registrar pago para un owner
   */
  async registerOwnerPayment(
    ownerId: string,
    paymentData: {
      amount: number;
      periodCovered: string;
      paymentMethod: string;
      referenceNumber?: string;
      receiptImageUrl?: string;
      adminNotes?: string;
    },
  ): Promise<any> {
    const authHeader = localStorage.getItem("admin_auth");

    const response = await fetch(`/api/admin/owners/${ownerId}/payments`, {
      method: "POST",
      headers: {
        Authorization: atob(authHeader || ""),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(paymentData),
    });

    if (!response.ok) {
      throw new Error("Error registrando pago");
    }

    const data = await response.json();
    return data.data;
  },

  /**
   * Obtener estadísticas de pagos
   */
  async getPaymentStats(): Promise<{
    totalPayments: number;
    totalAmount: number;
    verifiedPayments: number;
    pendingPayments: number;
    last30DaysAmount: number;
    paymentsByMonth: Array<{ month: string; amount: number }>;
  }> {
    const authHeader = localStorage.getItem("admin_auth");

    const response = await fetch(`/api/admin/payments/stats`, {
      headers: {
        Authorization: atob(authHeader || ""),
      },
    });

    if (!response.ok) {
      throw new Error("Error obteniendo estadísticas de pagos");
    }

    const data = await response.json();
    return data.data;
  },

  /**
   * Enviar notificación a owners
   */
  async sendOwnerNotification(data: {
    ownerIds: string[] | "all";
    title: string;
    message: string;
    type?: "info" | "warning" | "error" | "payment" | "system";
  }): Promise<{ success: boolean; message: string; data: any }> {
    const authHeader = localStorage.getItem("admin_auth");

    const response = await fetch(`/api/admin/owners/notifications`, {
      method: "POST",
      headers: {
        Authorization: atob(authHeader || ""),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ownerIds: data.ownerIds,
        title: data.title,
        message: data.message,
        type: data.type || "info",
        priority: data.type === "error" ? "high" : "normal",
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Error enviando notificaciones");
    }

    return await response.json();
  },

  /**
   * Obtener notificaciones de un owner
   */
  async getOwnerNotifications(ownerId: string): Promise<any[]> {
    const authHeader = localStorage.getItem("admin_auth");

    const response = await fetch(`/api/admin/owners/${ownerId}/notifications`, {
      headers: {
        Authorization: atob(authHeader || ""),
      },
    });

    if (!response.ok) {
      throw new Error("Error obteniendo notificaciones");
    }

    const data = await response.json();
    return data.data;
  },

  /**
   * Cambiar contraseña del administrador
   */
  async changePassword(data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<{ success: boolean; error?: string }> {
    const authHeader = localStorage.getItem("admin_auth");

    const response = await fetch("/api/admin/change-password", {
      method: "POST",
      headers: {
        Authorization: atob(authHeader || ""),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || "Error al cambiar la contraseña");
    }

    return result;
  },
};
